import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { HttpStatusCode } from "axios";
import { API_STATUS } from "../../sliceUtil";
import { _get, _post, _put } from "../../../../api/apiClient";
import { CustomError } from "../../../../api/CustomError";
import store from "../../../../app/store";
import { setLoaderFalse } from "../../../../Redux/loaderSlice";
import { CustomSuccess } from "../../../../api/CustomSuccess";

const initialSignUpState = {};

export const _orgClientId = () => {
  const state = store.getState();
  return state?.authReducer?.userData?.clientUserId;
};

export const validateEmailForFI = createAsyncThunk(
  "/auth/register",
  async (email, thunkAPI) => {
    const encodedMail = email.replaceAll("+", "%2B").trim();
    try {
      const config = {
        successMessage: "Email domain validated.",
        errorMessage: "Something went wrong.",
      };
      const response = await _get(
        `registration/validate/domain?emailId=${encodedMail}`,
        {},
        { metadata: { apiName: "validateEmailForFI" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const fetchInstutionsClientID = createAsyncThunk(
  "/auth/register",
  async (IDs, thunkAPI) => {
    try {
      const config = {
        successMessage: "Fetched client details",
        errorMessage: "Something went wrong.",
        disableToast: true,
      };
      const response = await _post(`registration/validate/client`, IDs, {
        ...config,
        metadata: { ...config?.metadata, apiName: "fetchInstutionsClientID" },
      });
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  },
);

export const activateUser = createAsyncThunk(
  "activateUser",
  async (userData, thunkAPI) => {
    try {
      const config = {
        successMessage: "User verified succssfully.",
        errorMessage: "Something went wrong.",
      };
      const encodedMail = userData?.emailId
        ?.replaceAll("+", "%2B")
        ?.replaceAll(" ", "%2B");
      const response = await _get(
        `/registration/verify?key=${userData?.key}&emailId=${encodedMail}`,
        {},
        {
          ...config,
          metadata: { ...config?.metadata, apiName: "activateUser" },
        },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);
export const signUpNewUser = createAsyncThunk(
  "/auth/register",
  async (signUpObj, thunkAPI) => {
    try {
      const config = {
        successMessage: "User registered successfully.",
        errorMessage: "Failed to register user.",
        isShowToast: true,
      };
      const response = await _post(`registration`, signUpObj, {
        ...config,
        metadata: { ...config?.metadata, apiName: "signUpNewUser" },
      });
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);

export const getUserProfileDetailsById = createAsyncThunk(
  "/auth/userProfile",
  async ({ disableLoader }, thunkAPI) => {
    try {
      const config = {
        successMessage: "User Profile details fetched successfully.",
        errorMessage: "Failed to fetch user details.",
        isShowToast: true,
      };
      const response = await _get(
        `clientuser/${_orgClientId()}`,
        {},
        {
          ...config,
          metadata: {
            ...config?.metadata,
            apiName: "getUserProfileDetailsById",
          },
        },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);
export const changePassword = createAsyncThunk(
  "/auth/changePassword",
  async ({ userBody, disableLoader }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Password changed successfully.",
        errorMessage: "Failed to change password.",
        isShowToast: true,
      };
      const response = await _put(`auth/changepassword`, userBody, {
        metadata: { apiName: "changePassword" },
      });
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);

export const createNewPassword = createAsyncThunk(
  "/auth/createNewPassword",
  async ({ userBody, disableLoader }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Password created successfully.",
        errorMessage: "Failed to create password.",
        isShowToast: true,
      };
      const response = await _put(`auth/updatepassword`, userBody, {
        metadata: { apiName: "createNewPassword" },
      });
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      return response;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);

export const resetPassword = createAsyncThunk(
  "/auth/resetPassword",
  async ({ emailId, disableLoader }, thunkAPI) => {
    try {
      const config = {
        successMessage: "Reset Password link sent on your registered Email ID.",
        errorMessage: "Failed to sent link.",
        isShowToast: true,
      };
      const encodedMail = emailId
        ?.replaceAll("+", "%2B")
        ?.replaceAll(" ", "%2B")
        ?.toLowerCase();
      const response = await _get(
        `/auth/resetpassword?emailId=${encodedMail}`,
        {},
        { metadata: { apiName: "resetPassword" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const { data } = CustomSuccess(response, {
        ...config,
      });
      return data;
    } catch (error) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(error?.error);
    }
  },
);

export const signUpSlice = createSlice({
  name: "signUpNewUser",
  initialState: initialSignUpState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(signUpNewUser.pending, () => ({
        isLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(signUpNewUser.fulfilled, (_state, action) => {
        return {
          isLoading: API_STATUS.STATUS_SUCCESS,
          signUpNewUser: action.payload,
        };
      })
      .addCase(signUpNewUser.rejected, (_state, action) => {
        return {
          isLoading: API_STATUS.STATUS_FAILURE,
          message: action.error.message || "Error while creating user",
          signUpNewUser: [],
        };
      });
  },
});
